/**
 * Tema do painel: padrão da LOJA x escolha do USUÁRIO (regra do dono, 23/10/2026).
 *
 *  - Configurações → Sistema → Aparência grava o padrão da loja
 *    (`settings.theme`, POST /api/settings). Vale para quem nunca escolheu.
 *  - O "Tema" do menu da foto grava só a escolha do usuário logado
 *    (`PUT /api/preferencias {tema}`), em qualquer aparelho dele.
 *
 * Antes os dois gravavam `settings.theme`: trocar pela foto mudava o tema de
 * todo mundo. O localStorage aqui é só cache (para não piscar ao abrir).
 */
import axios from "axios";

const API = "/api";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const CHAVE_LOJA = "tema-loja";
const chaveUsuario = (id) => `tema-usuario-${id}`;

const valido = (tema) => tema === "dark" || tema === "light";

function ler(chave) {
  try { return localStorage.getItem(chave); } catch { return null; }
}

function escrever(chave, valor) {
  try {
    if (valor) localStorage.setItem(chave, valor);
    else localStorage.removeItem(chave);
  } catch {
    // sem localStorage o tema só não fica em cache; o servidor guarda.
  }
}

function usuarioId() {
  try { return JSON.parse(localStorage.getItem("user") || "null")?.id || null; } catch { return null; }
}

/** Liga/desliga o tema escuro na tela e avisa quem escuta (Delivery, Mesas...). */
export function aplicarTema(tema) {
  const dark = tema === "dark";
  document.documentElement.classList.toggle("dark", dark);
  escrever("darkMode", String(dark));
  window.dispatchEvent(new CustomEvent("themeChange", { detail: { dark } }));
}

export function padraoDaLoja() {
  const t = ler(CHAVE_LOJA);
  return valido(t) ? t : null;
}

/** Chamado quando o /api/settings responde (boot e aba Aparência). */
export function guardarPadraoDaLoja(tema) {
  if (valido(tema)) escrever(CHAVE_LOJA, tema);
}

/** Escolha do usuário logado (cache) — null = segue o padrão da loja. */
export function escolhaDoUsuario() {
  const id = usuarioId();
  const t = id ? ler(chaveUsuario(id)) : null;
  return valido(t) ? t : null;
}

/** O tema que vale agora: o do usuário, senão o da loja. */
export function temaQueVale(padrao = padraoDaLoja()) {
  return escolhaDoUsuario() || (valido(padrao) ? padrao : null);
}

/** Busca a escolha do usuário no servidor e aplica (painel, após o login). */
export async function sincronizarTemaDoUsuario() {
  const id = usuarioId();
  if (!id) return;
  try {
    const { data } = await axios.get(`${API}/preferencias`, auth());
    escrever(chaveUsuario(id), valido(data?.tema) ? data.tema : null);
  } catch {
    return; // sem resposta: fica o que já está aplicado
  }
  const tema = temaQueVale();
  if (tema) aplicarTema(tema);
}

/** "Tema" do menu da foto: muda só para o usuário logado. */
export async function trocarTemaDoUsuario(tema) {
  const id = usuarioId();
  aplicarTema(tema);
  if (!id) return;
  escrever(chaveUsuario(id), tema);
  try {
    await axios.put(`${API}/preferencias`, { tema }, auth());
  } catch {
    // fica no cache deste aparelho; na próxima troca tenta de novo.
  }
}
